import React, { useState } from 'react';
import { CheckmarkCircleRegular } from '@fluentui/react-icons';

import { fmt } from '../ContractStatistics.Functions';
import * as Functions from './BillingControl.Functions';

interface IProps {
    groups: Functions.LeakGroup[];
    currency: string;
}

/** Cuántos casos se listan antes de cortar: más que esto no se lee, se exporta. */
const MAX_ITEMS = 50;

/**
 * Qué puede estar quedando afuera de la liquidación.
 *
 * Cada tarjeta es una pregunta concreta, con la cantidad de casos, la plata en juego
 * y qué hacer. Los casos se pueden abrir: decir "hay 14 problemas" sin poder ver
 * cuáles no le sirve a nadie.
 *
 * Los importes estimados se muestran como estimación, siempre. Un número inventado
 * con cara de dato es peor que no tener número.
 */
export const BillingLeaksComponent: React.FC<IProps> = ({ groups, currency }) => {
    const [openKey, setOpenKey] = useState<string | undefined>(undefined);
    const totals = Functions.leakTotals(groups);

    return (
        <div className="cs-card">
            <div className="cs-card__header">
                <div className="cs-card__title">Qué puede estar quedando afuera</div>
                <div className="cs-card__desc">
                    Cinco controles sobre el contrato. No todo lo que aparece acá es un error, pero todo lo que aparece
                    acá merece una mirada antes de cerrar la próxima liquidación.
                </div>
            </div>

            <div className="cs-kpi-grid">
                <div className="cs-kpi-cell">
                    <div className="cs-kpi-cell__value">{totals.cases}</div>
                    <div className="cs-kpi-cell__label">Casos para revisar</div>
                    <div className="cs-kpi-cell__desc">Sumando los cinco controles</div>
                </div>
                <div className="cs-kpi-cell">
                    <div className="cs-kpi-cell__value">
                        <span className={totals.certain > 0 ? 'fp-amount--negative' : undefined}>
                            {fmt(totals.certain, currency)}
                        </span>
                    </div>
                    <div className="cs-kpi-cell__label">Plata identificada</div>
                    <div className="cs-kpi-cell__desc">Importes reales de consumos ya cargados</div>
                </div>
                <div className="cs-kpi-cell">
                    <div className="cs-kpi-cell__value">{fmt(totals.estimated, currency)}</div>
                    <div className="cs-kpi-cell__label">Estimado no cargado</div>
                    <div className="cs-kpi-cell__desc">
                        Visitas hechas sin consumo, valuadas con lo que esa misma visita facturó en promedio
                    </div>
                </div>
            </div>

            <div className="cs-card__body">
                <div className="bc-leaks">
                    {groups.map((group) => {
                        const isOpen = openKey === group.key;
                        const isClean = group.count === 0;
                        const visible = group.items.slice(0, MAX_ITEMS);

                        return (
                            <div
                                key={group.key}
                                className={`bc-leak bc-leak--${isClean ? 'clean' : group.severity}`}
                            >
                                <span className="bc-leak__question">{group.question}</span>
                                <span className="bc-leak__title">{group.title}</span>

                                {isClean ? (
                                    <span className="bc-leak__clean">
                                        <CheckmarkCircleRegular /> Sin casos. Nada que revisar acá.
                                    </span>
                                ) : (
                                    <>
                                        <div className="bc-leak__figures">
                                            <span className="bc-leak__count">{group.count}</span>
                                            {group.amount > 0 && (
                                                <span
                                                    className={
                                                        'bc-leak__amount' +
                                                        (group.isEstimated ? ' bc-leak__amount--estimated' : '')
                                                    }
                                                >
                                                    {group.isEstimated ? '≈ ' : ''}
                                                    {fmt(group.amount, currency)}
                                                    {group.isEstimated ? ' estimados' : ''}
                                                </span>
                                            )}
                                        </div>
                                        <span className="bc-leak__action">{group.action}</span>

                                        <button
                                            type="button"
                                            className="bc-leak__toggle"
                                            aria-expanded={isOpen}
                                            onClick={() => setOpenKey(isOpen ? undefined : group.key)}
                                        >
                                            {isOpen ? 'Ocultar los casos' : `Ver los ${group.count} casos`}
                                        </button>

                                        {isOpen && (
                                            <ul className="bc-leak__list">
                                                {visible.map((item) => (
                                                    <li key={item.key} className="bc-leak__item">
                                                        <span>
                                                            <span className="bc-leak__item-title">{item.title}</span>
                                                            {item.detail && (
                                                                <span className="bc-leak__item-detail">{item.detail}</span>
                                                            )}
                                                        </span>
                                                        {item.amount !== undefined && item.amount > 0 && (
                                                            <span className="bc-leak__item-amount">
                                                                {item.isEstimated ? '≈ ' : ''}
                                                                {fmt(item.amount, currency)}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                                {group.items.length > visible.length && (
                                                    <li className="bc-leak__more">
                                                        y {group.items.length - visible.length} casos más. Descargá el
                                                        Excel para verlos todos.
                                                    </li>
                                                )}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default BillingLeaksComponent;
